/* The rule of forty, computed rather than asserted.
 *
 * Growth plus margin, and whether the two together clear forty. It is a
 * software-company yardstick and the app says so where it shows it — a memory
 * maker or a miner can fail it every year and be a fine business. Reported as
 * two numbers and their sum, never as a verdict on the company.
 *
 * Both halves are taken over trailing twelve months rather than a single
 * quarter, because one quarter of either is noise: a seasonal peak against a
 * seasonal trough would read as a company transformed. */

import type { StatementPeriod } from '../types';

export type MarginKind = 'operating' | 'net';

export interface RuleOf40 {
  growthPct: number;
  marginPct: number;
  total: number;
  clears: boolean;
  /** Which margin went into the total. The answer moves several points between
      the two, so which one was used has to travel with the number. */
  marginKind: MarginKind;
  /** The trailing year it covers, and the one it grew against. */
  from: string;
  to: string;
}

const QUARTERS = 4;

function sum(periods: StatementPeriod[], pick: (p: StatementPeriod) => number | null): number | null {
  let total = 0;
  for (const p of periods) {
    const v = pick(p);
    if (v === null) return null;        // a gap makes the year a guess, not a total
    total += v;
  }
  return total;
}

/** Null whenever there is not enough filed to answer honestly — eight quarters
    are needed, and every one of them has to carry a revenue figure. */
export function ruleOf40(quarterly: StatementPeriod[]): RuleOf40 | null {
  if (quarterly.length < QUARTERS * 2) return null;

  const ordered = [...quarterly].sort((a, b) => a.end.localeCompare(b.end));
  const recent = ordered.slice(-QUARTERS);
  const prior = ordered.slice(-QUARTERS * 2, -QUARTERS);

  const revenueNow = sum(recent, (p) => p.revenue);
  const revenueBefore = sum(prior, (p) => p.revenue);
  if (revenueNow === null || revenueBefore === null || revenueBefore === 0 || revenueNow <= 0) {
    return null;
  }

  /* Operating margin is the closer stand-in for the cash margin the rule was
     written around; net income carries tax and one-offs that say little about
     the business. Net is used only when operating income was not filed, and the
     card says which it was. */
  const operating = sum(recent, (p) => p.operatingIncome);
  const net = sum(recent, (p) => p.netIncome);
  const profit = operating ?? net;
  if (profit === null) return null;

  const growthPct = ((revenueNow - revenueBefore) / Math.abs(revenueBefore)) * 100;
  const marginPct = (profit / revenueNow) * 100;
  const total = growthPct + marginPct;

  return {
    growthPct,
    marginPct,
    total,
    clears: total >= 40,
    marginKind: operating === null ? 'net' : 'operating',
    from: prior[0]!.end,
    to: recent[QUARTERS - 1]!.end,
  };
}
